'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import { terraProxyApiClient } from '../../../clients/testerClient';
import type {
  TraceAgentFilter,
  TraceListGrouping,
  TraceListSort,
  TraceResultFilter,
  TraceStatusFilter,
} from '../../../store/tab/tracesTabScopedFilterStore';
import {
  EXECUTIONS_FETCH_ALL_PAGE_SIZE,
  EXECUTIONS_PAGE_SIZE,
  buildExecutionsListQuery,
  resolveNextExecutionsPageSkip,
  resolveSelectableExecutionTotal,
} from './executions-list.model';

const EXECUTIONS_LIST_QUERY_KEY = 'executions-list';

type ExecutionsListQuery = ReturnType<typeof buildExecutionsListQuery>;

const fetchExecutionsPage = async ({
  query,
  skip,
  limit,
}: {
  query: ExecutionsListQuery;
  skip: number;
  limit: number;
}) => {
  const response = await terraProxyApiClient.getTracesByApplication({
    ...query,
    skip,
    limit,
  });

  if (response.status !== 200) {
    throw new Error('Failed to fetch executions');
  }

  return response.body;
};

const fetchAllExecutions = async (
  query: ExecutionsListQuery,
): Promise<TracePreviewDto[]> => {
  const traces: TracePreviewDto[] = [];

  for (let skip = 0; ; skip += EXECUTIONS_FETCH_ALL_PAGE_SIZE) {
    const page = await fetchExecutionsPage({
      query,
      skip,
      limit: EXECUTIONS_FETCH_ALL_PAGE_SIZE,
    });

    const loadedBeforePage = traces.length;

    traces.push(...page.traces);

    if (
      traces.length === loadedBeforePage ||
      page.traces.length < EXECUTIONS_FETCH_ALL_PAGE_SIZE ||
      traces.length >= page.totalCount
    ) {
      return traces;
    }
  }
};

export function useExecutionsListData({
  applicationId,
  scopeKey,
  scopedEndpointIds,
  scopedUrlPatterns,
  isScopePending,
  searchTerm,
  statusFilter,
  resultFilter,
  agentFilter,
  createdSince,
  lifecycleState,
  sort,
  grouping,
  silentRefreshKey,
}: {
  applicationId: string | undefined;
  scopeKey: string;
  scopedEndpointIds: string[] | undefined;
  scopedUrlPatterns: string[] | undefined;
  isScopePending: boolean;
  searchTerm: string;
  statusFilter: readonly TraceStatusFilter[];
  resultFilter: readonly TraceResultFilter[];
  agentFilter: readonly TraceAgentFilter[];
  createdSince: Date | null;
  lifecycleState: LifecycleStateFilterDto | undefined;
  sort: TraceListSort;
  grouping: TraceListGrouping;
  silentRefreshKey: number;
}) {
  const listQuery = useMemo(
    () =>
      buildExecutionsListQuery({
        applicationId: applicationId ?? '',
        searchTerm,
        statusFilter,
        resultFilter,
        agentFilter,
        scopedEndpointIds,
        scopedUrlPatterns,
        createdSince,
        lifecycleState,
        sort,
        grouping,
      }),
    [
      agentFilter,
      applicationId,
      createdSince,
      grouping,
      lifecycleState,
      resultFilter,
      scopedEndpointIds,
      scopedUrlPatterns,
      searchTerm,
      sort,
      statusFilter,
    ],
  );
  const query = useInfiniteQuery({
    queryKey: [
      EXECUTIONS_LIST_QUERY_KEY,
      applicationId,
      scopeKey,
      searchTerm,
      statusFilter.join(','),
      resultFilter.join(','),
      agentFilter.join(','),
      createdSince?.toISOString() ?? '',
      lifecycleState ?? '',
      sort?.colId ?? '',
      sort?.sort ?? '',
      grouping?.field ?? '',
      grouping?.order ?? '',
    ],
    queryFn: ({ pageParam }) =>
      fetchExecutionsPage({
        query: listQuery,
        skip: pageParam,
        limit: EXECUTIONS_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (_lastPage, pages) =>
      resolveNextExecutionsPageSkip({ pages }),
    enabled: !!applicationId && !isScopePending,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === applicationId
        ? keepPreviousData(previousData)
        : undefined,
  });
  const { refetch, fetchNextPage, isPlaceholderData, isFetching } = query;
  const previousSilentRefreshKeyRef = useRef(silentRefreshKey);

  useEffect(() => {
    if (previousSilentRefreshKeyRef.current === silentRefreshKey) {
      return;
    }

    previousSilentRefreshKeyRef.current = silentRefreshKey;
    void refetch();
  }, [refetch, silentRefreshKey]);

  const displayedTotalCount = query.data?.pages[0]?.totalCount ?? 0;
  const selectableTotalQuery = useQuery({
    queryKey: [
      EXECUTIONS_LIST_QUERY_KEY,
      'selectable-total',
      applicationId,
      scopeKey,
      searchTerm,
      statusFilter.join(','),
      resultFilter.join(','),
      agentFilter.join(','),
      createdSince?.toISOString() ?? '',
      sort?.colId ?? '',
      sort?.sort ?? '',
      grouping?.field ?? '',
      grouping?.order ?? '',
      silentRefreshKey,
    ],
    queryFn: () =>
      fetchExecutionsPage({
        query: { ...listQuery, lifecycleState: 'active' },
        skip: 0,
        limit: 1,
      }),
    enabled: !!applicationId && !isScopePending && lifecycleState === 'all',
    refetchOnWindowFocus: false,
  });
  const traces = useMemo(
    () => query.data?.pages.flatMap((page) => page.traces) ?? [],
    [query.data],
  );
  const loadMore = useCallback(() => {
    if (isPlaceholderData || isFetching) {
      return;
    }

    void fetchNextPage();
  }, [fetchNextPage, isFetching, isPlaceholderData]);
  const fetchAllTraces = useCallback(
    () => fetchAllExecutions(listQuery),
    [listQuery],
  );

  return {
    traces,
    fetchAllTraces,
    totalCount: displayedTotalCount,
    selectableTotalCount: resolveSelectableExecutionTotal({
      displayedTotalCount,
      lifecycleState,
      mutableTotalCount: selectableTotalQuery.data?.totalCount,
    }),
    isInitialLoading:
      !!applicationId &&
      query.isPending &&
      (isScopePending || query.isFetching),
    isFetchingMore: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    loadMore,
  };
}
